// Import hooks.
import {
	useInputForm,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import api models.
import {
	Address,
	PossibleState,
} from '@app/models/api';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';
// Import props.
import {
	InputAddressWithoutInputTextBoxProps,
	InputFormProps,
} from '@app/props';
// Import services.
import {
	AddressService,
} from '@app/services';

interface UseInputAddressState {
	addressSingleLine: string;
	address?: Address;
}

interface UseInputAddressParameters {
	id: string;
	label: string;
	infoText?: string;
	required: boolean;
	disabled: boolean;
	initialInput?: UseInputAddressState;
	showValidation?: boolean;
	possibleStates: PossibleState[];
	onGetAddressSuggestions: (query: string) => Promise<Address[]>;
}

interface UseInputAddressResults<T = unknown> extends UseInputFormResult<UseInputAddressState, InputAddressWithoutInputTextBoxProps & T> {
	setAddress: (address: Address) => void;
}

const useInputAddress = (params: UseInputAddressParameters): UseInputAddressResults => {

	// #region exported methods.

	/**
	 * Event handler for processing address value changes.
	 * @param address
	 */
	const setAddress = (address: Address): void => {
		other.setInput({
			address: address,
			addressSingleLine: String.empty(),
		});
	}

	/**
	 * Event handler for processing single line address value changes.
	 * @param address
	 */
	const setSingleLineAddress = (address: string): void => {
		other.setInput({
			address: undefined,
			addressSingleLine: address,
		});
	}

	/**
	 * Returns props for form input. 
	 **/
	const applyAddressProps = (): InputAddressWithoutInputTextBoxProps => {
		// First apply the form input props.
		let formProps: InputFormProps = applyProps();
		// Then apply the address props.
		return {
			...formProps,
			infoText: params.infoText,
			address: other.value.address !== undefined
				? other.value.address
				: other.value.addressSingleLine,
			onChangeSingleLineAddress: setSingleLineAddress,
			onSelectAddressSuggestion: setAddress,
			onGetAddressSuggestions: params.onGetAddressSuggestions,
		}
	};

	// #endregion exported methods.


	// #region private methods.

	/**
	 * Validates the address.
	 * @param addressState
	 * @param required
	 */
	const validateAddress = (state: UseInputAddressState, required: boolean): ValidatorResponse => {
		if (required
			&& String.isNullOrWhiteSpace(state.addressSingleLine)
			&& state.address === undefined) {
			return new ValidatorResponse(false, 'Address is required.');
		}

		return new ValidatorResponse(true, String.empty());
	}

	/**
	 * Validates the address components.
	 * @param addressState
	 * @param required
	 */
	const validateAddressComponents = (state: UseInputAddressState, required: boolean): ValidatorResponse => {
		let messages: string[] = [];
		let isValid: boolean = true;

		if (state.address !== undefined) {
			// Validate address line 1.
			if (String.isNullOrWhiteSpace(state.address.addressLine1)) {
				messages.push('Street address is missing.');
				isValid = false;
			}

			// Validate the city.
			if (String.isNullOrWhiteSpace(state.address.city)) {
				messages.push('City is missing.');
				isValid = false;
			}

			// Validate the state.
			const stateCode: string | undefined = state.address.state ?? undefined;
			if (stateCode === undefined
				|| String.isNullOrWhiteSpace(stateCode)) {
				messages.push('State is missing.');
				isValid = false;
			}
			else if (params.possibleStates.find(possibleState => possibleState.code === stateCode || possibleState.name === stateCode) === undefined) {
				messages.push('Address state is invalid.');
				isValid = false;
			}

			// Validate the postal code.
			if (String.isNullOrWhiteSpace(state.address.postalCode)) {
				messages.push('Postal code is missing.');
				isValid = false;
			}
			else if (!AddressService.getInstance().isStringZipFormat(state.address.postalCode ?? String.empty())) {
				messages.push('Postal code is invalid.');
				isValid = false;
			}
		}

		return new ValidatorResponse(isValid, messages.join("  "));
	}

	// #endregion private methods.


	// #region variables

	// Instantiate the 'address' input.
	const { applyProps, ...other } = useInputForm<UseInputAddressState>({
		id: params.id,
		label: params.label,
		required: params.required,
		disabled: params.disabled,
		initialInput: params.initialInput ?? { address: undefined, addressSingleLine: String.empty() },
		showValidation: params.showValidation,
		validators: [validateAddress, validateAddressComponents],
	});

	// #endregion variables


	return {
		applyProps: applyAddressProps,
		setAddress: setAddress,
		...other,
	};
}

// Export parameters.
export {
	UseInputAddressParameters,
};

// Export results.
export {
	UseInputAddressResults,
};

// Export state.
export {
	UseInputAddressState,
};

export default useInputAddress;